import React, { useState, useEffect } from "react";
import { Lightbulb, Sparkles, BookOpen } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Fallback cultural facts if no articles in DB
const fallbackFacts = [
  {
    title: "Le premier DJ Hip Hop",
    content: "DJ Kool Herc, considéré comme le père du Hip Hop, a inventé le 'breakbeat' en 1973 lors d'une fête dans le Bronx. Il isolait et répétait les breaks instrumentaux pour que les danseurs puissent s'exprimer plus longtemps.",
    category: "Hip-Hop/RNB",
    author: "TIKA Team"
  },
  {
    title: "Fela Kuti, le pionnier rebelle",
    content: "Fela Kuti a créé l'Afrobeat dans les années 70 en mélangeant le jazz, le funk et les rythmes yoruba. Il utilisait sa musique comme arme politique contre la dictature militaire nigériane.",
    category: "Afrobeat",
    author: "TIKA Team"
  },
  {
    title: "Les racines du R&B",
    content: "Le terme 'Rhythm and Blues' a été créé en 1949 par Jerry Wexler. Le R&B est né de la fusion du blues, du gospel et du jazz afro-américain, devenant la base de nombreux genres modernes.",
    category: "Hip-Hop/RNB",
    author: "TIKA Team"
  },
  {
    title: "Le Dancehall jamaïcain",
    content: "Le dancehall est né dans les années 70 dans les sound systems de Kingston. Ce genre a révolutionné la musique caribéenne et influence aujourd'hui des artistes du monde entier.",
    category: "Dancehall/Caraïbes",
    author: "TIKA Team"
  },
  {
    title: "La battle culture",
    content: "Les battles de rap ont remplacé les conflits de gangs dans le Bronx des années 70. Au lieu de se battre physiquement, les crews s'affrontaient avec des rimes, créant une culture de compétition créative.",
    category: "Hip-Hop/RNB",
    author: "TIKA Team"
  }
];

export const DidYouKnow = ({ category, orderId }) => {
  const [article, setArticle] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRandomArticle();
  }, [category, orderId]);

  const fetchRandomArticle = async () => {
    try {
      // Fetch all articles from DB
      const response = await axios.get(`${API}/culture?limit=100`);
      const dbArticles = response.data || [];

      let articlesToChooseFrom = dbArticles;

      // If no articles in DB, use fallback facts
      if (articlesToChooseFrom.length === 0) {
        articlesToChooseFrom = fallbackFacts;
      }

      // Filter by category if specified (but keep some variety)
      let filteredArticles = articlesToChooseFrom;
      if (category && category !== "all") {
        const categoryArticles = articlesToChooseFrom.filter(
          a => a.category === category || a.category === "Général"
        );
        // If we have category-specific articles, use them, otherwise use all
        if (categoryArticles.length > 0) {
          filteredArticles = categoryArticles;
        }
      }

      // Generate a truly random index for each display
      // Each customer sees a different random fact
      const randomIndex = Math.floor(Math.random() * filteredArticles.length);

      const selectedArticle = filteredArticles[randomIndex];
      setArticle(selectedArticle);

      // Animate in after a short delay
      setTimeout(() => {
        setIsVisible(true);
      }, 300);
    } catch (error) {
      console.error("Erreur lors du chargement de l'article:", error);
      // Use fallback
      const randomFallback = fallbackFacts[Math.floor(Math.random() * fallbackFacts.length)];
      setArticle(randomFallback);
      setTimeout(() => setIsVisible(true), 300);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-card rounded-lg p-6 border-2 border-primary/30 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
        <div className="h-3 bg-muted rounded w-full mb-2"></div>
        <div className="h-3 bg-muted rounded w-2/3"></div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div 
      className={`relative glass-card rounded-lg p-6 border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 transition-all duration-700 transform ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
      data-testid="did-you-know-card"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
              <Lightbulb className="w-7 h-7 text-primary" />
            </div>
            <Sparkles className="w-5 h-5 text-primary absolute -top-1 -right-1 animate-pulse" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="text-lg font-bold text-primary uppercase tracking-wide">
              Le savais-tu ?
            </h3>
            {article.category && (
              <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-semibold rounded">
                {article.category}
              </span>
            )}
          </div>
          
          <h4 className="font-bold text-foreground mb-2 text-base">
            {article.title}
          </h4>
          
          <p className="text-muted-foreground text-sm leading-relaxed">
            {article.content || article.fact}
          </p>

          {article.author && (
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/30">
              <BookOpen className="w-4 h-4 text-primary/60" />
              <span className="text-xs text-muted-foreground">
                Par {article.author}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full blur-2xl -z-10" />
    </div>
  );
};
